import { Injectable, inject } from '@angular/core';
import { RegisterUserFormService } from 'src/app/pages/register-user/services/register-user-form.service';
import { DEFAULT_TIME } from '../config/constants';

@Injectable({
  providedIn: 'root'
})
export class TimeFieldService {
  // SERVICE INJECTIONS
  private _registerUserFormService = inject(RegisterUserFormService);

  // SERVICE STATE SIGNALS
  private _startTime = this._registerUserFormService.startTime;
  private _endTime = this._registerUserFormService.endTime;
  
  // METHODS
  /**
   * Determines if the given control name represents the start time field
   * @param controlName - The form control name to check
   * @returns True if this is the start time field
   */
  isStartTimeField(controlName: string | null): boolean {
    return controlName === 'startTime';
  }

  /**
   * Gets the current value for the specified time field
   * @param controlName - The form control name
   * @returns The current time value or empty string if not set
   */
  getCurrentTimeValue(controlName: string | null): string {
    if (this.isStartTimeField(controlName)) {
      return this._startTime() || DEFAULT_TIME;
    } else {
      return this._endTime() || DEFAULT_TIME;
    }
  }

  /**
   * Gets the appropriate label for the time field
   * @param controlName - The form control name
   * @returns The localized label key for the field
   */
  getFieldLabel(controlName: string | null): string {
    return this.isStartTimeField(controlName) 
      ? 'Hora inicio de la ruta' 
      : 'Hora fin de la ruta';
  }

  /**
   * Updates the time value in the form service
   * @param controlName - The form control name
   * @param value - The new time value
   */
  updateTimeValue(controlName: string | null, value: string): void {
    if (this.isStartTimeField(controlName)) {
      this._registerUserFormService.setStartTime(value);
    } else {
      this._registerUserFormService.setEndTime(value);
    }
  }

  /**
   * Validates if the provided time is within acceptable range
   * @param time - The time string to validate (HH:MM format)
   * @returns True if the time is valid
   */
  isValidTimeFormat(time: string): boolean {
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  }

  /**
   * Converts time string to minutes for comparison
   * @param time - Time in HH:MM format
   * @returns Total minutes since midnight
   */
  timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Validates time range consistency (start time should be before end time)
   * @returns Object with validation result and error message if invalid
   */
  validateTimeRange(): { isValid: boolean; errorMessage?: string } {
    const start = this._startTime();
    const end = this._endTime();

    if (!start || !end) {
      return { isValid: true }; // Incomplete ranges are valid (fields are optional)
    }

    if (!this.isValidTimeFormat(start) || !this.isValidTimeFormat(end)) {
      return { 
        isValid: false, 
        errorMessage: 'Invalid time format. Please use HH:MM format.' 
      };
    }

    const startMinutes = this.timeToMinutes(start);
    const endMinutes = this.timeToMinutes(end);

    if (startMinutes >= endMinutes) {
      return { 
        isValid: false, 
        errorMessage: 'Start time must be before end time.' 
      };
    }

    return { isValid: true };
  }

  /**
   * Gets the time range duration in minutes
   * @returns Duration in minutes, or null if either time is missing
   */
  getTimeRangeDuration(): number | null {
    const start = this._startTime();
    const end = this._endTime();

    if (!start || !end || !this.isValidTimeFormat(start) || !this.isValidTimeFormat(end)) {
      return null;
    }

    const startMinutes = this.timeToMinutes(start);
    const endMinutes = this.timeToMinutes(end);

    return endMinutes - startMinutes;
  }

  /**
   * Resets all time values to default state
   */
  resetTimeValues(): void {
    this._registerUserFormService.setStartTime(DEFAULT_TIME);
    this._registerUserFormService.setEndTime(DEFAULT_TIME);
  }
}
